import DownloadIcon from '@mui/icons-material/Download';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  LinearProgress,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
} from '@mui/material';
import { ResponsiveTreeMap } from '@nivo/treemap';
import Papa from 'papaparse';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { ValueType } from 'recharts/types/component/DefaultTooltipContent';
import { useLazyGetTranscriptCoverageCsvQuery } from './api/transcriptCoverageApi';
import { downloadAdminCsv } from './utils/downloadAdminCsv';

// ─── Types ────────────────────────────────────────────────────────────────────

interface CoverageRow {
  coach_person_id: string;
  coach_email: string;
  coach_name: string;
  member_person_id: string;
  member_email: string;
  member_name: string;
  partner_name: string;
  session_started_at: string;
  session_title: string;
  session_status: string;
  classification: string;
  has_transcript_likely: boolean;
}

interface BreakdownItem {
  label: string;
  total: number;
  transcribed: number;
  rate: number;
}

interface MemberStats {
  memberKey: string;
  memberPersonId: string;
  memberEmail: string;
  memberName: string;
  coachName: string;
  coachEmail: string;
  partnerName: string;
  domain: string;
  totalSessions: number;
  transcribedSessions: number;
  coverageRate: number;
  everTranscribed: boolean;
  fullyTranscribed: boolean;
  lastSessionTranscribed: boolean;
  currentTranscribedStreak: number;
  lastSessionAt: string;
  // Sessions in chronological order (oldest → newest). Used by the per-row
  // sparkline so admins can see the actual pattern (who recently improved,
  // who recently broke, who's chronically flaky) at a glance.
  sessionsAsc: { date: string; transcribed: boolean }[];
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function emailDomain(email: string): string {
  return email?.includes('@') ? email.split('@')[1] : email || '(unknown)';
}

// Buddy sessions are internal training: two Mento staff practice on each
// other during coach onboarding. The structured training program is roughly
// the first BUDDY_TRAINING_SESSION_COUNT sessions of a coach.
//
// Detection rule (intersection of two signals):
//   1. Both coach_email and member_email at @mento.co.
//   2. The session is among the coach's first N sessions chronologically.
//
// Both must hold. (1) on its own would also catch legitimate internal
// sessions between long-tenured staff, which aren't training. (2) on its
// own would drop early customer sessions of new coaches.
const BUDDY_TRAINING_SESSION_COUNT = 10;

function bothEmailsAtMento(r: CoverageRow): boolean {
  const coachAtMento = (r.coach_email || '').toLowerCase().endsWith('@mento.co');
  const memberAtMento = (r.member_email || '').toLowerCase().endsWith('@mento.co');
  return coachAtMento && memberAtMento;
}

// buildBuddySessionSet returns the rows that count as buddy/training. It
// needs the FULL row history (not the filtered slice) so the "first N" is
// stable even when the dashboard's date window starts mid-coach-tenure.
function buildBuddySessionSet(rows: CoverageRow[]): Set<CoverageRow> {
  const out = new Set<CoverageRow>();
  if (rows.length === 0) return out;
  const byCoach = new Map<string, CoverageRow[]>();
  for (const r of rows) {
    const key = r.coach_person_id || r.coach_email;
    if (!key) continue;
    const list = byCoach.get(key);
    if (list) list.push(r);
    else byCoach.set(key, [r]);
  }
  for (const arr of byCoach.values()) {
    arr.sort((a, b) => (a.session_started_at < b.session_started_at ? -1 : 1));
    const limit = Math.min(arr.length, BUDDY_TRAINING_SESSION_COUNT);
    for (let i = 0; i < limit; i++) {
      if (bothEmailsAtMento(arr[i])) out.add(arr[i]);
    }
  }
  return out;
}

function toPercent(rate: number): string {
  return `${Math.round(rate * 100)}%`;
}

function buildBreakdown(rows: CoverageRow[], key: (r: CoverageRow) => string): BreakdownItem[] {
  const map = new Map<string, { total: number; transcribed: number }>();
  for (const r of rows) {
    const label = key(r) || '(unknown)';
    const existing = map.get(label) ?? { total: 0, transcribed: 0 };
    map.set(label, {
      total: existing.total + 1,
      transcribed: existing.transcribed + (r.has_transcript_likely ? 1 : 0),
    });
  }
  return Array.from(map.entries())
    .map(([label, { total, transcribed }]) => ({
      label,
      total,
      transcribed,
      rate: total > 0 ? transcribed / total : 0,
    }))
    .sort((a, b) => b.total - a.total);
}

function buildMemberStats(rows: CoverageRow[]): MemberStats[] {
  const byMember = new Map<string, CoverageRow[]>();
  for (const r of rows) {
    const key = r.member_person_id || r.member_email;
    if (!key) continue;
    const list = byMember.get(key) ?? [];
    list.push(r);
    byMember.set(key, list);
  }

  const out: MemberStats[] = [];
  for (const [key, memberRows] of byMember) {
    // Newest first
    const sorted = [...memberRows].sort((a, b) =>
      a.session_started_at < b.session_started_at ? 1 : -1
    );
    const total = sorted.length;
    const transcribed = sorted.filter((r) => r.has_transcript_likely).length;

    // Consecutive transcribed sessions counting backward from the latest.
    let streak = 0;
    for (const r of sorted) {
      if (r.has_transcript_likely) streak++;
      else break;
    }

    const latest = sorted[0];
    // sorted is newest-first; reverse for chronological display.
    const sessionsAsc = sorted
      .slice()
      .reverse()
      .map((r) => ({ date: r.session_started_at, transcribed: r.has_transcript_likely }));
    out.push({
      memberKey: key,
      memberPersonId: latest.member_person_id,
      memberEmail: latest.member_email,
      memberName: latest.member_name,
      coachName: latest.coach_name || latest.coach_email,
      coachEmail: latest.coach_email,
      partnerName: latest.partner_name,
      domain: emailDomain(latest.member_email),
      totalSessions: total,
      transcribedSessions: transcribed,
      coverageRate: total > 0 ? transcribed / total : 0,
      everTranscribed: transcribed > 0,
      fullyTranscribed: total > 0 && transcribed === total,
      lastSessionTranscribed: latest.has_transcript_likely,
      currentTranscribedStreak: streak,
      lastSessionAt: latest.session_started_at,
      sessionsAsc,
    });
  }
  return out;
}

const COVERAGE_BUCKETS = [
  '0%',
  '1–10%',
  '11–20%',
  '21–30%',
  '31–40%',
  '41–50%',
  '51–60%',
  '61–70%',
  '71–80%',
  '81–90%',
  '91–99%',
  '100%',
] as const;

// Deciles are (lower, upper] with a small epsilon to avoid FP edge cases where
// 0.3 * 10 ≠ 3 exactly. A rate of exactly 0.30 falls into "21–30%".
function bucketForRate(rate: number): (typeof COVERAGE_BUCKETS)[number] {
  if (rate <= 0) return '0%';
  if (rate >= 1) return '100%';
  const deciles: (typeof COVERAGE_BUCKETS)[number][] = [
    '1–10%',
    '11–20%',
    '21–30%',
    '31–40%',
    '41–50%',
    '51–60%',
    '61–70%',
    '71–80%',
    '81–90%',
    '91–99%',
  ];
  for (let i = 0; i < deciles.length; i++) {
    if (rate <= (i + 1) / 10 + 1e-9) return deciles[i];
  }
  return '91–99%';
}

function memberCoverageDistribution(
  members: MemberStats[]
): { bucket: string; count: number; pct: number }[] {
  const counts = new Map<string, number>(COVERAGE_BUCKETS.map((b) => [b, 0]));
  for (const m of members) {
    const b = bucketForRate(m.coverageRate);
    counts.set(b, (counts.get(b) ?? 0) + 1);
  }
  const total = members.length;
  return COVERAGE_BUCKETS.map((bucket) => {
    const count = counts.get(bucket) ?? 0;
    return { bucket, count, pct: total > 0 ? count / total : 0 };
  });
}

const NEUTRAL_BAR_COLOR = '#6b7a8d';

// ─── Activation trend (over time) ────────────────────────────────────────────

interface ActivationPoint {
  weekStart: string; // ISO date of the Monday anchoring this week
  rate: number; // % of coaches activated (Strong adopter or Champion) as of week end
  activatedCount: number;
  totalCoaches: number;
}

// buildActivationTrend computes the activation % at each weekly snapshot,
// accumulating each coach's session history up to that week. A coach
// counts as "activated" at week T if their cumulative rate (transcribed /
// total sessions up to T) is ≥ 70% (Strong adopter or Champion threshold).
//
// We bucket weeks Monday-anchored to stay consistent with the weekly
// transcription rate chart elsewhere on the page.
function buildActivationTrend(rows: CoverageRow[]): ActivationPoint[] {
  if (rows.length === 0) return [];
  const sorted = [...rows].sort((a, b) => (a.session_started_at < b.session_started_at ? -1 : 1));

  const weekStart = (iso: string): Date => {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return new Date(0);
    const utc = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
    const offsetToMonday = (utc.getUTCDay() + 6) % 7;
    utc.setUTCDate(utc.getUTCDate() - offsetToMonday);
    return utc;
  };

  // Collect all weeks present in the data so the trend covers the whole window.
  const weekKeys = new Set<string>();
  for (const r of sorted) {
    const w = weekStart(r.session_started_at).toISOString().slice(0, 10);
    if (w !== '1970-01-01') weekKeys.add(w);
  }
  const weeks = [...weekKeys].sort();
  if (weeks.length === 0) return [];

  const coachState = new Map<string, { total: number; transcribed: number }>();
  const out: ActivationPoint[] = [];
  let cursor = 0;
  for (const wk of weeks) {
    // Advance cursor through all rows whose week ≤ this week (inclusive).
    while (cursor < sorted.length) {
      const rWeek = weekStart(sorted[cursor].session_started_at).toISOString().slice(0, 10);
      if (rWeek > wk) break;
      const r = sorted[cursor];
      const key = r.coach_person_id || r.coach_email;
      if (key) {
        const s = coachState.get(key) ?? { total: 0, transcribed: 0 };
        s.total += 1;
        if (r.has_transcript_likely) s.transcribed += 1;
        coachState.set(key, s);
      }
      cursor += 1;
    }
    // Snapshot at end of this week.
    let activated = 0;
    let total = 0;
    for (const s of coachState.values()) {
      if (s.total === 0) continue;
      total += 1;
      if (s.transcribed / s.total >= 0.7) activated += 1;
    }
    out.push({
      weekStart: wk,
      rate: total > 0 ? activated / total : 0,
      activatedCount: activated,
      totalCoaches: total,
    });
  }
  return out;
}

// ─── Coach segmentation ──────────────────────────────────────────────────────
//
// Thresholds derived from the activation spreadsheet (coach_transcription_segments.xlsx):
//   Never started          rate == 0
//   Tried but didn't stick 0 < rate < 30%
//   Partial adopter        30% ≤ rate < 70%
//   Strong adopter         70% ≤ rate < 90%
//   Champion               rate ≥ 90%
//
// "Activated / power user" = Strong adopter + Champion. The org target is 75%.

type CoachSegment =
  | 'Never started'
  | "Tried but didn't stick"
  | 'Partial adopter'
  | 'Strong adopter'
  | 'Champion';

const SEGMENTS: CoachSegment[] = [
  'Never started',
  "Tried but didn't stick",
  'Partial adopter',
  'Strong adopter',
  'Champion',
];

const ACTIVATED_SEGMENTS: CoachSegment[] = ['Strong adopter', 'Champion'];
const ACTIVATED_TARGET = 0.75; // 75% goal

type SegmentBreakdown = {
  coaches: number;
  sessions: number;
  transcribed: number;
};

function emptySegmentBreakdown(): Map<CoachSegment, SegmentBreakdown> {
  return new Map(SEGMENTS.map((segment) => [segment, { coaches: 0, sessions: 0, transcribed: 0 }]));
}

const SEGMENT_COLORS: Record<CoachSegment, string> = {
  'Never started': '#9ca3af', // grey
  "Tried but didn't stick": '#fb923c', // orange
  'Partial adopter': '#facc15', // yellow
  'Strong adopter': '#84cc16', // lime
  Champion: '#637a6f', // sage/500 — Mento brand primary (matches selected pills)
};

function assignSegment(rate: number): CoachSegment {
  if (rate <= 0) return 'Never started';
  if (rate < 0.3) return "Tried but didn't stick";
  if (rate < 0.7) return 'Partial adopter';
  if (rate < 0.9) return 'Strong adopter';
  return 'Champion';
}

interface CoachStats {
  coachKey: string;
  coachName: string;
  coachEmail: string;
  totalSessions: number;
  transcribedSessions: number;
  rate: number;
  segment: CoachSegment;
  memberCount: number;
}

function buildCoachStats(rows: CoverageRow[]): CoachStats[] {
  const acc = new Map<
    string,
    {
      coachName: string;
      coachEmail: string;
      total: number;
      transcribed: number;
      members: Set<string>;
    }
  >();
  for (const r of rows) {
    const key = r.coach_person_id || r.coach_email;
    if (!key) continue;
    let a = acc.get(key);
    if (!a) {
      a = {
        coachName: r.coach_name || r.coach_email || '(unknown)',
        coachEmail: r.coach_email,
        total: 0,
        transcribed: 0,
        members: new Set<string>(),
      };
      acc.set(key, a);
    }
    a.total += 1;
    if (r.has_transcript_likely) a.transcribed += 1;
    const memberKey = r.member_person_id || r.member_email;
    if (memberKey) a.members.add(memberKey);
  }
  const out: CoachStats[] = [];
  for (const [key, a] of acc) {
    const rate = a.total > 0 ? a.transcribed / a.total : 0;
    out.push({
      coachKey: key,
      coachName: a.coachName,
      coachEmail: a.coachEmail,
      totalSessions: a.total,
      transcribedSessions: a.transcribed,
      rate,
      segment: assignSegment(rate),
      memberCount: a.members.size,
    });
  }
  return out;
}

// readableTextColor returns either dark text or white text depending on
// the perceived luminance of the cell background. ~140 threshold puts
// mid-range cells in dark text and the high-rate (darker green) cells
// in white text, which is what reads best on the treemap.
function readableTextColor(rgbString: string): string {
  const m = /rgb\((\d+),\s*(\d+),\s*(\d+)\)/.exec(rgbString);
  if (!m) return '#1f2937';
  const r = +m[1];
  const g = +m[2];
  const b = +m[3];
  // Standard Rec. 601 luma approximation. Good enough for "is this dark?".
  const luma = 0.299 * r + 0.587 * g + 0.114 * b;
  return luma < 140 ? '#ffffff' : '#1f2937';
}

// rateColor interpolates between sage/200 (#edf2f0) and sage/500 (#637a6f,
// Mento's brand primary green) for the treemap. The 100% endpoint matches
// the selected-pill color and the Champion segment chip exactly, so
// "fully covered" reads as the same brand-correct sage wherever it appears.
function rateColor(rate: number): string {
  const t = Math.max(0, Math.min(1, rate));
  const r1 = 0xed;
  const g1 = 0xf2;
  const b1 = 0xf0;
  const r2 = 0x63;
  const g2 = 0x7a;
  const b2 = 0x6f;
  const r = Math.round(r1 + (r2 - r1) * t);
  const g = Math.round(g1 + (g2 - g1) * t);
  const b = Math.round(b1 + (b2 - b1) * t);
  return `rgb(${r},${g},${b})`;
}

// ─── Weekly trend (aggregate view) ───────────────────────────────────────────

interface WeeklyRatePoint {
  weekStart: string; // ISO date of the Monday anchoring this week
  total: number;
  transcribed: number;
  rate: number; // 0..1
}

function buildWeeklyTrend(rows: CoverageRow[]): WeeklyRatePoint[] {
  if (rows.length === 0) return [];
  const byWeek = new Map<string, { total: number; transcribed: number }>();
  for (const r of rows) {
    const d = new Date(r.session_started_at);
    if (Number.isNaN(d.getTime())) continue;
    // Anchor to Monday (ISO week start). getUTCDay: Sun=0..Sat=6.
    const utc = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
    const offsetToMonday = (utc.getUTCDay() + 6) % 7;
    utc.setUTCDate(utc.getUTCDate() - offsetToMonday);
    const key = utc.toISOString().slice(0, 10);
    const slot = byWeek.get(key) ?? { total: 0, transcribed: 0 };
    slot.total += 1;
    if (r.has_transcript_likely) slot.transcribed += 1;
    byWeek.set(key, slot);
  }
  return Array.from(byWeek.entries())
    .map(([weekStart, v]) => ({
      weekStart,
      total: v.total,
      transcribed: v.transcribed,
      rate: v.total > 0 ? v.transcribed / v.total : 0,
    }))
    .sort((a, b) => (a.weekStart < b.weekStart ? -1 : 1));
}

// ─── Per-member sparkline ─────────────────────────────────────────────────────

/**
 * Renders a member's session-by-session transcription pattern as a row of
 * small dots, oldest on the left, newest on the right. Green = transcribed,
 * grey = not. Hover (via the `title` attribute) shows the session date.
 *
 * We cap to the most recent N sessions so the column width is bounded.
 */
function MemberSparkline({
  sessions,
  max = 20,
}: {
  sessions: { date: string; transcribed: boolean }[];
  max?: number;
}) {
  const visible = sessions.length > max ? sessions.slice(sessions.length - max) : sessions;
  const truncated = sessions.length - visible.length;
  if (visible.length === 0) {
    return (
      <Typography variant="caption" color="text.secondary">
        —
      </Typography>
    );
  }
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {truncated > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
          +{truncated}…
        </Typography>
      )}
      <Box sx={{ display: 'flex', gap: '2px', alignItems: 'center' }}>
        {visible.map((s, i) => (
          <Box
            key={i}
            title={`${s.date.slice(0, 10)} — ${s.transcribed ? 'transcribed' : 'not transcribed'}`}
            sx={{
              width: 7,
              height: 7,
              borderRadius: '50%',
              bgcolor: s.transcribed ? 'success.main' : 'grey.300',
              flexShrink: 0,
            }}
          />
        ))}
      </Box>
    </Box>
  );
}

// ─── Date range presets ──────────────────────────────────────────────────────

type DateRangePreset = '30d' | '2m' | '6m' | '12m' | 'all';

const DATE_PRESETS: readonly { key: DateRangePreset; label: string }[] = [
  { key: '30d', label: 'Last 30 days' },
  { key: '2m', label: 'Last 2 months' },
  { key: '6m', label: 'Last 6 months' },
  { key: '12m', label: 'Last 12 months' },
  { key: 'all', label: 'Since Jul 31, 2025' },
] as const;

const DEFAULT_DATE_PRESET: DateRangePreset = '2m';

function presetFromDate(preset: DateRangePreset): string {
  if (preset === 'all') return '';
  const now = new Date();
  let target: Date;
  if (preset === '30d') {
    target = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 30);
  } else {
    const months = preset === '2m' ? 2 : preset === '6m' ? 6 : 12;
    // Avoid Date.setMonth() day-overflow: on e.g. April 30,
    // setMonth(month-2) would target Feb 30, which JS silently rolls
    // forward to March 2 — shrinking "Last 2 months" by ~26 days.
    // Clamp the day to one that exists in the target month.
    const targetYearMonth = new Date(now.getFullYear(), now.getMonth() - months, 1);
    const lastDayOfTargetMonth = new Date(
      targetYearMonth.getFullYear(),
      targetYearMonth.getMonth() + 1,
      0
    ).getDate();
    target = new Date(
      targetYearMonth.getFullYear(),
      targetYearMonth.getMonth(),
      Math.min(now.getDate(), lastDayOfTargetMonth)
    );
  }
  // Use local-time components to build YYYY-MM-DD. toISOString() would
  // shift the day across the TZ boundary on dates near midnight.
  const yyyy = target.getFullYear();
  const mm = String(target.getMonth() + 1).padStart(2, '0');
  const dd = String(target.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function KpiCard({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, minWidth: 160, flex: '1 1 160px' }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h5">{value}</Typography>
      {sub && (
        <Typography variant="caption" color="text.secondary">
          {sub}
        </Typography>
      )}
    </Paper>
  );
}

type ChartSortKey = 'total' | 'rate' | 'label';
type ChartView = 'rate' | 'totals';

function BreakdownChart({ title, data }: { title: string; data: BreakdownItem[] }) {
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<ChartSortKey>('total');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [view, setView] = useState<ChartView>('rate');

  function toggleSort(key: ChartSortKey) {
    if (key === sortKey) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir(key === 'label' ? 'asc' : 'desc');
    }
  }

  const processed = useMemo(() => {
    let items = search
      ? data.filter((d) => d.label.toLowerCase().includes(search.toLowerCase()))
      : data;
    items = [...items].sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'total') cmp = a.total - b.total;
      else if (sortKey === 'rate') cmp = a.rate - b.rate;
      else cmp = a.label.localeCompare(b.label);
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return items;
  }, [data, search, sortKey, sortDir]);

  const SortBtn = ({ k, label }: { k: ChartSortKey; label: string }) => (
    <Button
      size="small"
      variant={sortKey === k ? 'contained' : 'outlined'}
      onClick={() => toggleSort(k)}
      sx={{ minWidth: 0, px: 1, py: 0.25, fontSize: 11 }}
    >
      {label} {sortKey === k ? (sortDir === 'asc' ? '↑' : '↓') : ''}
    </Button>
  );

  const ROW_H = 32;
  const MAX_VISIBLE = 10;
  const fullHeight = Math.max(200, processed.length * ROW_H);
  const clampedHeight = Math.min(fullHeight, MAX_VISIBLE * ROW_H);
  const needsScroll = processed.length > MAX_VISIBLE;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      {/* Header row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle1">{title}</Typography>
          {/* View toggle */}
          <Box
            sx={{
              display: 'flex',
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              overflow: 'hidden',
            }}
          >
            {(['rate', 'totals'] as ChartView[]).map((v) => (
              <Box
                key={v}
                onClick={() => setView(v)}
                sx={{
                  px: 1.25,
                  py: 0.25,
                  fontSize: 11,
                  cursor: 'pointer',
                  userSelect: 'none',
                  bgcolor: view === v ? 'action.selected' : 'transparent',
                  fontWeight: view === v ? 700 : 400,
                  '&:hover': { bgcolor: 'action.hover' },
                  textTransform: 'capitalize',
                }}
              >
                {v === 'rate' ? 'Rate %' : 'Totals'}
              </Box>
            ))}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Search…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ width: 130, '& .MuiInputBase-input': { py: 0.5, fontSize: 12 } }}
          />
          <SortBtn k="total" label="Sessions" />
          <SortBtn k="rate" label="Rate" />
          <SortBtn k="label" label="A–Z" />
        </Box>
      </Box>

      {/* Legend for totals view */}
      {view === 'totals' && processed.length > 0 && (
        <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: '#6b7a8d' }} />
            <Typography variant="caption">Transcribed</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: '#d1d5db' }} />
            <Typography variant="caption">Not transcribed</Typography>
          </Box>
        </Box>
      )}

      {processed.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
          No results
        </Typography>
      ) : (
        <>
          <Box
            sx={{
              overflowY: needsScroll ? 'auto' : 'visible',
              maxHeight: clampedHeight,
              border: needsScroll ? 1 : 0,
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            <Box sx={{ height: fullHeight, minWidth: 0 }}>
              <ResponsiveContainer width="100%" height={fullHeight}>
                {view === 'rate' ? (
                  <BarChart
                    data={processed}
                    layout="vertical"
                    margin={{ left: 8, right: 40, top: 4, bottom: 4 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis
                      type="number"
                      domain={[0, 1]}
                      tickFormatter={(v) => `${Math.round(v * 100)}%`}
                    />
                    <YAxis type="category" dataKey="label" width={180} tick={{ fontSize: 12 }} />
                    <Tooltip
                      formatter={(value: ValueType | undefined, _name, props) => [
                        value != null
                          ? `${toPercent(Number(value))} (${props.payload.transcribed}/${props.payload.total})`
                          : '—',
                        'Transcription rate',
                      ]}
                    />
                    <Bar dataKey="rate" fill={NEUTRAL_BAR_COLOR} radius={[0, 4, 4, 0]} />
                  </BarChart>
                ) : (
                  <BarChart
                    data={processed.map((d) => ({ ...d, notTranscribed: d.total - d.transcribed }))}
                    layout="vertical"
                    margin={{ left: 8, right: 40, top: 4, bottom: 4 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis type="number" allowDecimals={false} />
                    <YAxis type="category" dataKey="label" width={180} tick={{ fontSize: 12 }} />
                    <Tooltip
                      formatter={(value: ValueType | undefined, name) => [
                        value ?? 0,
                        name === 'transcribed' ? 'Transcribed' : 'Not transcribed',
                      ]}
                    />
                    <Bar dataKey="transcribed" stackId="a" fill={NEUTRAL_BAR_COLOR} />
                    <Bar
                      dataKey="notTranscribed"
                      stackId="a"
                      fill="#d1d5db"
                      radius={[0, 4, 4, 0]}
                    />
                  </BarChart>
                )}
              </ResponsiveContainer>
            </Box>
          </Box>
          {needsScroll && (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
              Showing {processed.length} items — scroll to see all
            </Typography>
          )}
        </>
      )}
    </Paper>
  );
}

// ─── Data Summary ─────────────────────────────────────────────────────────────

function DataSummary({
  filtered,
  rows,
  memberCount,
  dateFrom,
  dateTo,
  selectedCoaches,
  selectedDomains,
  selectedPartners,
  selectedStatuses,
  search,
}: {
  filtered: CoverageRow[];
  rows: CoverageRow[];
  memberCount: number;
  dateFrom: string;
  dateTo: string;
  selectedCoaches: string[];
  selectedDomains: string[];
  selectedPartners: string[];
  selectedStatuses: string[];
  search: string;
}) {
  const parts: string[] = [];

  // Session count vs total
  if (filtered.length === rows.length) {
    parts.push(`All ${rows.length.toLocaleString()} sessions`);
  } else {
    parts.push(`${filtered.length.toLocaleString()} of ${rows.length.toLocaleString()} sessions`);
  }

  parts.push(`${memberCount.toLocaleString()} member${memberCount !== 1 ? 's' : ''}`);

  // Date range
  if (dateFrom || dateTo) {
    const fmt = (d: string) =>
      new Date(d).toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    if (dateFrom && dateTo) parts.push(`${fmt(dateFrom)} – ${fmt(dateTo)}`);
    else if (dateFrom) parts.push(`from ${fmt(dateFrom)}`);
    else if (dateTo) parts.push(`up to ${fmt(dateTo)}`);
  } else {
    // Show actual date range from the data
    const dates = filtered
      .map((r) => r.session_started_at)
      .filter(Boolean)
      .sort();
    if (dates.length > 1) {
      const fmt = (d: string) =>
        new Date(d).toLocaleDateString(undefined, {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        });
      parts.push(`${fmt(dates[0])} – ${fmt(dates[dates.length - 1])}`);
    }
  }

  // Coaches
  const coachCount = new Set(filtered.map((r) => r.coach_name || r.coach_email)).size;
  if (selectedCoaches.length)
    parts.push(`${selectedCoaches.length} coach${selectedCoaches.length !== 1 ? 'es' : ''}`);
  else parts.push(`${coachCount} coach${coachCount !== 1 ? 'es' : ''}`);

  // Partners
  const partnerCount = new Set(filtered.map((r) => r.partner_name).filter(Boolean)).size;
  if (selectedPartners.length)
    parts.push(`${selectedPartners.length} partner${selectedPartners.length !== 1 ? 's' : ''}`);
  else parts.push(`${partnerCount} partner${partnerCount !== 1 ? 's' : ''}`);

  // Domains
  const domainCount = new Set(filtered.map((r) => emailDomain(r.member_email))).size;
  if (selectedDomains.length)
    parts.push(`${selectedDomains.length} domain${selectedDomains.length !== 1 ? 's' : ''}`);
  else parts.push(`${domainCount} domain${domainCount !== 1 ? 's' : ''}`);

  // Search
  if (search) parts.push(`matching "${search}"`);

  // Statuses
  if (selectedStatuses.length) parts.push(selectedStatuses.join(', '));

  return (
    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
      {parts.join(' · ')}
    </Typography>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

type SortField = keyof Pick<
  CoverageRow,
  'session_started_at' | 'coach_name' | 'member_name' | 'partner_name' | 'session_status'
>;

type MemberSortField =
  | 'memberName'
  | 'coachName'
  | 'partnerName'
  | 'totalSessions'
  | 'transcribedSessions'
  | 'coverageRate'
  | 'currentTranscribedStreak'
  | 'lastSessionAt';

type Dimension = 'coach' | 'domain' | 'partner';

function parseCsvText(text: string): CoverageRow[] {
  const { data } = Papa.parse<Record<string, string>>(text, { header: true, skipEmptyLines: true });
  return data.map((r) => ({
    coach_person_id: r.coach_person_id ?? '',
    coach_email: r.coach_email ?? '',
    coach_name: r.coach_name ?? '',
    member_person_id: r.member_person_id ?? '',
    member_email: r.member_email ?? '',
    member_name: r.member_name ?? '',
    partner_name: r.partner_name ?? '',
    session_started_at: r.session_started_at ?? '',
    session_title: r.session_title ?? '',
    session_status: r.session_status ?? '',
    classification: r.classification ?? '',
    has_transcript_likely: r.has_transcript_likely === 'true',
  }));
}

export default function TranscriptCoveragePage() {
  const [rows, setRows] = useState<CoverageRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mockFileName, setMockFileName] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Filters
  const [activePreset, setActivePreset] = useState<DateRangePreset | null>(DEFAULT_DATE_PRESET);
  const [dateFrom, setDateFrom] = useState<string>(() => presetFromDate(DEFAULT_DATE_PRESET));
  const [dateTo, setDateTo] = useState('');
  // Default: exclude coaches whose sessions are 100% buddy/training. They
  // can be toggled back on to see the unfiltered numbers.
  const [includeBuddy, setIncludeBuddy] = useState(false);
  const [selectedCoaches, setSelectedCoaches] = useState<string[]>([]);
  const [selectedDomains, setSelectedDomains] = useState<string[]>([]);
  const [selectedPartners, setSelectedPartners] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [search, setSearch] = useState('');

  // Dimension picker (for the session-rate breakdown chart)
  const [dimension, setDimension] = useState<Dimension>('coach');

  // Coach segmentation filter — clicking a segment card sets this and drives
  // the Coaches table + filters the treemap to that segment.
  const [selectedSegment, setSelectedSegment] = useState<CoachSegment | null>(null);

  // Treemap sort order. Affects placement of cells inside the chart.
  // Note: cell SIZE is always sessions; sort only changes order/placement.
  type TreemapSort = 'sessions' | 'rate-desc' | 'rate-asc';
  const [treemapSort, setTreemapSort] = useState<TreemapSort>('sessions');

  // Coaches table sort (defaults to sessions desc — same as treemap default).
  type CoachSortField =
    | 'coachName'
    | 'segment'
    | 'memberCount'
    | 'totalSessions'
    | 'transcribedSessions'
    | 'rate';
  const [coachSortField, setCoachSortField] = useState<CoachSortField>('totalSessions');
  const [coachSortDir, setCoachSortDir] = useState<'asc' | 'desc'>('desc');

  // Members table
  const [memberSortField, setMemberSortField] = useState<MemberSortField>('coverageRate');
  const [memberSortDir, setMemberSortDir] = useState<'asc' | 'desc'>('asc');
  const [memberPage, setMemberPage] = useState(0);
  const memberPerPage = 50;

  // Sessions table
  const [sortField, setSortField] = useState<SortField>('session_started_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);
  const perPage = 50;
  const [downloadError, setDownloadError] = useState<string | null>(null);

  // ── Load data ──────────────────────────────────────────────────────────────

  const loaded = useRef(false);
  const mockLoaded = useRef(false);
  const [getTranscriptCoverageCsv] = useLazyGetTranscriptCoverageCsvQuery();

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const text = await getTranscriptCoverageCsv().unwrap();
      if (!mockLoaded.current) setRows(parseCsvText(text));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [getTranscriptCoverageCsv]);

  useEffect(() => {
    if (loaded.current) return;
    loaded.current = true;
    void loadData();
  }, [loadData]);

  function handleMockFileLoad(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result as string;
      mockLoaded.current = true;
      setRows(parseCsvText(text));
      setMockFileName(file.name);
      setError(null);
      setLoading(false);
      setPage(0);
      setMemberPage(0);
    };
    reader.readAsText(file);
    // Reset so the same file can be reloaded
    e.target.value = '';
  }

  // ── Filter options ─────────────────────────────────────────────────────────

  const coaches = useMemo(
    () => [...new Set(rows.map((r) => r.coach_name || r.coach_email))].sort(),
    [rows]
  );
  const domains = useMemo(
    () => [...new Set(rows.map((r) => emailDomain(r.member_email)))].sort(),
    [rows]
  );
  const partners = useMemo(
    () => [...new Set(rows.map((r) => r.partner_name || '(unknown)'))].sort(),
    [rows]
  );
  const statuses = useMemo(() => [...new Set(rows.map((r) => r.session_status))].sort(), [rows]);

  // ── Filtered rows ──────────────────────────────────────────────────────────

  // baseFiltered = rows after the user-facing filters (date, coach, domain,
  // etc.). Computed separately so we can identify buddy-only coaches BEFORE
  // applying the buddy exclusion in `filtered`.
  const baseFiltered = useMemo(() => {
    return rows.filter((r) => {
      if (dateFrom && r.session_started_at < dateFrom) return false;
      if (dateTo && r.session_started_at > dateTo + 'T99') return false;
      if (selectedCoaches.length && !selectedCoaches.includes(r.coach_name || r.coach_email))
        return false;
      if (selectedDomains.length && !selectedDomains.includes(emailDomain(r.member_email)))
        return false;
      if (selectedPartners.length && !selectedPartners.includes(r.partner_name || '(unknown)'))
        return false;
      if (selectedStatuses.length && !selectedStatuses.includes(r.session_status)) return false;
      if (search) {
        const q = search.toLowerCase();
        if (
          !r.coach_name.toLowerCase().includes(q) &&
          !r.coach_email.toLowerCase().includes(q) &&
          !r.member_name.toLowerCase().includes(q) &&
          !r.member_email.toLowerCase().includes(q) &&
          !r.session_title.toLowerCase().includes(q)
        )
          return false;
      }
      return true;
    });
  }, [
    rows,
    dateFrom,
    dateTo,
    selectedCoaches,
    selectedDomains,
    selectedPartners,
    selectedStatuses,
    search,
  ]);

  // Buddy session set computed once from the FULL row history, so the
  // "first N" boundary per coach is stable regardless of the dashboard's
  // date window. This is what the exclusion uses.
  const buddySessions = useMemo(() => buildBuddySessionSet(rows), [rows]);

  // How many buddy sessions are within the current base-filter window —
  // for the UI hint about what's being hidden.
  const buddyHiddenSessions = useMemo(() => {
    if (buddySessions.size === 0) return 0;
    let n = 0;
    for (const r of baseFiltered) if (buddySessions.has(r)) n += 1;
    return n;
  }, [baseFiltered, buddySessions]);

  // How many distinct coaches have at least one buddy session in window —
  // useful context for the user.
  const buddyAffectedCoaches = useMemo(() => {
    if (buddySessions.size === 0) return 0;
    const set = new Set<string>();
    for (const r of baseFiltered) {
      if (!buddySessions.has(r)) continue;
      const key = r.coach_person_id || r.coach_email;
      if (key) set.add(key);
    }
    return set.size;
  }, [baseFiltered, buddySessions]);

  // Final filtered set used by all downstream metrics and tables.
  // Removing buddy sessions naturally drops coaches who only have buddy
  // sessions (they end up with 0 sessions and disappear from coach stats).
  const filtered = useMemo(() => {
    if (includeBuddy || buddySessions.size === 0) return baseFiltered;
    return baseFiltered.filter((r) => !buddySessions.has(r));
  }, [baseFiltered, buddySessions, includeBuddy]);

  // ── Member stats ───────────────────────────────────────────────────────────

  const memberStats = useMemo(() => buildMemberStats(filtered), [filtered]);

  const memberKpis = useMemo(() => {
    const total = memberStats.length;
    if (total === 0) {
      return {
        total: 0,
        everCount: 0,
        everRate: 0,
        fullCount: 0,
        fullRate: 0,
        avgRate: 0,
        lastCount: 0,
        lastRate: 0,
      };
    }
    const ever = memberStats.filter((m) => m.everTranscribed).length;
    const full = memberStats.filter((m) => m.fullyTranscribed).length;
    const last = memberStats.filter((m) => m.lastSessionTranscribed).length;
    const avg = memberStats.reduce((s, m) => s + m.coverageRate, 0) / total;
    return {
      total,
      everCount: ever,
      everRate: ever / total,
      fullCount: full,
      fullRate: full / total,
      avgRate: avg,
      lastCount: last,
      lastRate: last / total,
    };
  }, [memberStats]);

  const coverageDistribution = useMemo(
    () => memberCoverageDistribution(memberStats),
    [memberStats]
  );

  const weeklyTrend = useMemo(() => buildWeeklyTrend(filtered), [filtered]);

  // ── Coach segmentation ─────────────────────────────────────────────────────

  const coachStats = useMemo(() => buildCoachStats(filtered), [filtered]);

  const segmentBreakdown = useMemo(() => {
    const out = emptySegmentBreakdown();
    for (const coachStat of coachStats) {
      const bucket = out.get(coachStat.segment);
      if (!bucket) {
        continue;
      }
      bucket.coaches += 1;
      bucket.sessions += coachStat.totalSessions;
      bucket.transcribed += coachStat.transcribedSessions;
    }
    return out;
  }, [coachStats]);

  const activatedCount = coachStats.filter((c) => ACTIVATED_SEGMENTS.includes(c.segment)).length;
  const activatedRate = coachStats.length > 0 ? activatedCount / coachStats.length : 0;

  // Activation rate over time — cumulative through the filter window.
  // Respects the buddy-session exclusion because it uses `filtered`.
  const activationTrend = useMemo(() => buildActivationTrend(filtered), [filtered]);

  const visibleCoaches = useMemo(() => {
    const list = selectedSegment
      ? coachStats.filter((c) => c.segment === selectedSegment)
      : coachStats;
    return [...list].sort((a, b) => {
      let cmp = 0;
      if (coachSortField === 'segment') {
        // Use the canonical segment progression (Never started → Champion),
        // not alphabetical, so the table sort respects the segmentation order.
        cmp = SEGMENTS.indexOf(a.segment) - SEGMENTS.indexOf(b.segment);
      } else if (coachSortField === 'coachName') {
        cmp = a.coachName.localeCompare(b.coachName);
      } else {
        cmp = (a[coachSortField] as number) - (b[coachSortField] as number);
      }
      return coachSortDir === 'asc' ? cmp : -cmp;
    });
  }, [coachStats, selectedSegment, coachSortField, coachSortDir]);

  function handleCoachSort(field: CoachSortField) {
    if (field === coachSortField) {
      setCoachSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setCoachSortField(field);
      // Numeric fields default to desc (most → least);
      // text/segment defaults to asc (A→Z or Never started → Champion).
      setCoachSortDir(field === 'coachName' || field === 'segment' ? 'asc' : 'desc');
    }
  }

  // Nivo expects a hierarchical root with a children array. Each leaf gets
  // its own color via the `colors` callback in the JSX, which reads `rate`.
  //
  // d3-hierarchy does NOT sort by default — without an explicit sort, cells
  // appear in input order, which makes squarify produce ugly columnar
  // layouts. We always sort, and the sort key is user-controlled.
  //
  // Selecting a segment narrows the treemap to that segment's coaches so
  // you can study the subset in isolation.
  const treemapData = useMemo(() => {
    let list = selectedSegment
      ? coachStats.filter((c) => c.segment === selectedSegment)
      : [...coachStats];
    list = [...list];
    switch (treemapSort) {
      case 'rate-desc':
        list.sort((a, b) => b.rate - a.rate || b.totalSessions - a.totalSessions);
        break;
      case 'rate-asc':
        list.sort((a, b) => a.rate - b.rate || b.totalSessions - a.totalSessions);
        break;
      default:
        list.sort((a, b) => b.totalSessions - a.totalSessions);
    }
    return {
      name: 'coaches',
      children: list.map((c) => ({
        id: c.coachKey,
        name: c.coachName,
        value: c.totalSessions,
        rate: c.rate,
        transcribed: c.transcribedSessions,
        segment: c.segment,
      })),
    };
  }, [coachStats, selectedSegment, treemapSort]);

  // ── Session-level context ──────────────────────────────────────────────────

  const totalSessions = filtered.length;
  const transcribed = filtered.filter((r) => r.has_transcript_likely).length;
  const rate = totalSessions > 0 ? transcribed / totalSessions : 0;

  // ── Breakdowns (for dimension picker chart) ────────────────────────────────

  const byCoach = useMemo(
    () => buildBreakdown(filtered, (r) => r.coach_name || r.coach_email),
    [filtered]
  );
  const byDomain = useMemo(
    () => buildBreakdown(filtered, (r) => emailDomain(r.member_email)),
    [filtered]
  );
  const byPartner = useMemo(
    () => buildBreakdown(filtered, (r) => r.partner_name || '(unknown)'),
    [filtered]
  );

  const dimensionTitle =
    dimension === 'coach'
      ? 'Session rate by coach'
      : dimension === 'domain'
        ? 'Session rate by member email domain'
        : 'Session rate by partner';
  const dimensionData =
    dimension === 'coach' ? byCoach : dimension === 'domain' ? byDomain : byPartner;

  // ── Sorted sessions ────────────────────────────────────────────────────────

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const av = a[sortField] ?? '';
      const bv = b[sortField] ?? '';
      const cmp = av < bv ? -1 : av > bv ? 1 : 0;
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [filtered, sortField, sortDir]);

  const pageRows = sorted.slice(page * perPage, (page + 1) * perPage);
  const totalPages = Math.ceil(sorted.length / perPage);

  // ── Sorted members ─────────────────────────────────────────────────────────

  const sortedMembers = useMemo(() => {
    return [...memberStats].sort((a, b) => {
      const av = a[memberSortField];
      const bv = b[memberSortField];
      let cmp = 0;
      if (typeof av === 'number' && typeof bv === 'number') cmp = av - bv;
      else cmp = String(av).localeCompare(String(bv));
      return memberSortDir === 'asc' ? cmp : -cmp;
    });
  }, [memberStats, memberSortField, memberSortDir]);

  const memberPageRows = sortedMembers.slice(
    memberPage * memberPerPage,
    (memberPage + 1) * memberPerPage
  );
  const memberTotalPages = Math.ceil(sortedMembers.length / memberPerPage);

  function handleSort(field: SortField) {
    if (field === sortField) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('desc');
    }
    setPage(0);
  }

  function handleMemberSort(field: MemberSortField) {
    if (field === memberSortField) {
      setMemberSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setMemberSortField(field);
      // Coverage defaults to ascending (worst first — most actionable).
      setMemberSortDir(field === 'coverageRate' ? 'asc' : 'desc');
    }
    setMemberPage(0);
  }

  function applyDatePreset(p: DateRangePreset) {
    setActivePreset(p);
    setDateFrom(presetFromDate(p));
    setDateTo(''); // open-ended on the upper end — captures "up to now"
    setPage(0);
    setMemberPage(0);
  }

  async function handleDownload() {
    setDownloadError(null);
    try {
      await downloadAdminCsv({
        target: 'admin.coaches.transcript_coverage_csv',
      });
    } catch (e) {
      setDownloadError(e instanceof Error ? e.message : 'Download failed');
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !import.meta.env.DEV) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert
          severity="error"
          action={
            <Button
              size="small"
              onClick={() => {
                loaded.current = false;
                void loadData();
              }}
            >
              Retry
            </Button>
          }
        >
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1400, margin: '0 auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Typography variant="h5">Transcript Coverage</Typography>
        <Button
          variant="outlined"
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
          size="small"
        >
          Download full history CSV
        </Button>
      </Box>
      {downloadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {downloadError}
        </Alert>
      )}

      {import.meta.env.DEV && (
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            style={{ display: 'none' }}
            onChange={handleMockFileLoad}
          />
          <Alert
            severity={mockFileName ? 'success' : 'info'}
            sx={{ mb: 2 }}
            action={
              <Button size="small" onClick={() => fileInputRef.current?.click()}>
                {mockFileName ? 'Replace' : 'Load CSV'}
              </Button>
            }
          >
            {mockFileName
              ? `Using local file: ${mockFileName}`
              : 'Optional: load a CSV from your computer to replace this tab’s report. Otherwise, the report uses the local backend. Nothing is uploaded.'}
          </Alert>
        </>
      )}

      {/* Filters */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
          Filters
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>
            Range:
          </Typography>
          {DATE_PRESETS.map((p) => (
            <Chip
              key={p.key}
              label={p.label}
              size="small"
              variant={activePreset === p.key ? 'filled' : 'outlined'}
              color={activePreset === p.key ? 'primary' : 'default'}
              onClick={() => applyDatePreset(p.key)}
            />
          ))}
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
          <TextField
            label="From"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={dateFrom}
            onChange={(e) => {
              setDateFrom(e.target.value);
              setActivePreset(null);
              setPage(0);
              setMemberPage(0);
            }}
            sx={{ width: 160 }}
          />
          <TextField
            label="To"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            value={dateTo}
            onChange={(e) => {
              setDateTo(e.target.value);
              setActivePreset(null);
              setPage(0);
              setMemberPage(0);
            }}
            sx={{ width: 160 }}
          />
          <MultiSelect
            label="Coach"
            options={coaches}
            value={selectedCoaches}
            onChange={(v) => {
              setSelectedCoaches(v);
              setPage(0);
              setMemberPage(0);
            }}
          />
          <MultiSelect
            label="Member domain"
            options={domains}
            value={selectedDomains}
            onChange={(v) => {
              setSelectedDomains(v);
              setPage(0);
              setMemberPage(0);
            }}
          />
          <MultiSelect
            label="Partner"
            options={partners}
            value={selectedPartners}
            onChange={(v) => {
              setSelectedPartners(v);
              setPage(0);
              setMemberPage(0);
            }}
          />
          <MultiSelect
            label="Status"
            options={statuses}
            value={selectedStatuses}
            onChange={(v) => {
              setSelectedStatuses(v);
              setPage(0);
              setMemberPage(0);
            }}
          />
          <TextField
            label="Search"
            size="small"
            placeholder="Coach or member…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
              setMemberPage(0);
            }}
            sx={{ width: 220 }}
          />
          {(activePreset !== DEFAULT_DATE_PRESET ||
            includeBuddy ||
            selectedCoaches.length ||
            selectedDomains.length ||
            selectedPartners.length ||
            selectedStatuses.length ||
            search) && (
            <Button
              size="small"
              onClick={() => {
                setActivePreset(DEFAULT_DATE_PRESET);
                setDateFrom(presetFromDate(DEFAULT_DATE_PRESET));
                setDateTo('');
                setIncludeBuddy(false);
                setSelectedCoaches([]);
                setSelectedDomains([]);
                setSelectedPartners([]);
                setSelectedStatuses([]);
                setSearch('');
                setPage(0);
                setMemberPage(0);
              }}
            >
              Reset to defaults
            </Button>
          )}
        </Box>

        {/* Buddy session exclusion toggle */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
          <Chip
            size="small"
            clickable
            color={includeBuddy ? 'default' : 'primary'}
            variant={includeBuddy ? 'outlined' : 'filled'}
            label={
              includeBuddy
                ? 'Buddy/training sessions: included'
                : 'Buddy/training sessions: excluded (default)'
            }
            onClick={() => {
              setIncludeBuddy((v) => !v);
              setPage(0);
              setMemberPage(0);
            }}
          />
          <Typography variant="caption" color="text.secondary">
            {buddyHiddenSessions > 0
              ? `${buddyHiddenSessions} buddy session${buddyHiddenSessions === 1 ? '' : 's'} across ${buddyAffectedCoaches} coach${buddyAffectedCoaches === 1 ? '' : 'es'} in the current window. ${includeBuddy ? 'Currently shown.' : 'Currently hidden.'} Rule: both attendees @mento.co AND among the coach's first ${BUDDY_TRAINING_SESSION_COUNT} sessions.`
              : 'No buddy/training sessions detected in this window.'}
          </Typography>
        </Box>
      </Paper>

      {/* Data summary */}
      {rows.length > 0 && (
        <DataSummary
          filtered={filtered}
          rows={rows}
          memberCount={memberStats.length}
          dateFrom={dateFrom}
          dateTo={dateTo}
          selectedCoaches={selectedCoaches}
          selectedDomains={selectedDomains}
          selectedPartners={selectedPartners}
          selectedStatuses={selectedStatuses}
          search={search}
        />
      )}

      {/* Coach activation headline */}
      <Paper variant="outlined" sx={{ p: 3, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, flexWrap: 'wrap', mb: 1.5 }}>
          <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>
            Coach activation
          </Typography>
          <Typography variant="h3">{toPercent(activatedRate)}</Typography>
          <Typography variant="body2" color="text.secondary">
            Strong adopter + Champion · {activatedCount} of {coachStats.length} coaches · target{' '}
            {toPercent(ACTIVATED_TARGET)}
          </Typography>
        </Box>
        <Box sx={{ position: 'relative', mt: 1, mb: 2.5 }}>
          <LinearProgress
            variant="determinate"
            value={Math.min(100, activatedRate * 100)}
            sx={{
              height: 12,
              borderRadius: 1,
              bgcolor: 'grey.200',
              '& .MuiLinearProgress-bar': { bgcolor: SEGMENT_COLORS.Champion },
            }}
          />
          {/* Target tick */}
          <Box
            sx={{
              position: 'absolute',
              left: `${ACTIVATED_TARGET * 100}%`,
              top: -3,
              bottom: -3,
              width: '2px',
              bgcolor: 'text.primary',
              transform: 'translateX(-1px)',
            }}
          />
          <Typography
            variant="caption"
            sx={{
              position: 'absolute',
              left: `${ACTIVATED_TARGET * 100}%`,
              top: 14,
              transform: 'translateX(-50%)',
              color: 'text.secondary',
            }}
          >
            target {toPercent(ACTIVATED_TARGET)}
          </Typography>
        </Box>

        {/* Activation trend over time */}
        {activationTrend.length > 1 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Activation trend (cumulative; weekly snapshots within the filtered window)
            </Typography>
            <ResponsiveContainer width="100%" height={140}>
              <LineChart data={activationTrend} margin={{ left: 8, right: 16, top: 4, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis
                  dataKey="weekStart"
                  tick={{ fontSize: 10 }}
                  interval="preserveStartEnd"
                  tickFormatter={(v: string) => v.slice(5)}
                />
                <YAxis
                  domain={[0, 1]}
                  tickFormatter={(v) => `${Math.round(Number(v) * 100)}%`}
                  tick={{ fontSize: 10 }}
                  width={40}
                />
                <Tooltip
                  formatter={(value: ValueType | undefined, _name, props) => {
                    // The target reference line shares the same Tooltip
                    // but should NOT render the activated/total fraction —
                    // doing so produces contradictory math like
                    // "75% (13/20)" where 13/20 is the actual week rate.
                    if (props.dataKey === 'target') {
                      return [toPercent(Number(value)), 'Target'];
                    }
                    return [
                      value != null
                        ? `${toPercent(Number(value))} (${props.payload.activatedCount}/${props.payload.totalCoaches})`
                        : '—',
                      'Activated',
                    ];
                  }}
                  labelFormatter={(label) => `Week of ${label}`}
                />
                {/* Target reference line */}
                <Line
                  type="monotone"
                  data={activationTrend.map((p) => ({ ...p, target: ACTIVATED_TARGET }))}
                  dataKey="target"
                  stroke="#000"
                  strokeDasharray="3 3"
                  strokeWidth={1}
                  dot={false}
                  isAnimationActive={false}
                  legendType="none"
                />
                <Line
                  type="monotone"
                  dataKey="rate"
                  stroke={SEGMENT_COLORS.Champion}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                  isAnimationActive={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        )}
      </Paper>

      {/* Segment breakdown cards (click to filter the Coaches table + treemap) */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(5, 1fr)' },
          gap: 2,
          mb: 3,
        }}
      >
        {SEGMENTS.map((seg) => {
          const b = segmentBreakdown.get(seg) ?? { coaches: 0, sessions: 0, transcribed: 0 };
          const selected = selectedSegment === seg;
          const pct = coachStats.length > 0 ? b.coaches / coachStats.length : 0;
          return (
            <Paper
              key={seg}
              variant="outlined"
              onClick={() => setSelectedSegment(selected ? null : seg)}
              sx={{
                p: 2,
                cursor: 'pointer',
                borderColor: selected ? SEGMENT_COLORS[seg] : 'divider',
                borderWidth: selected ? 2 : 1,
                bgcolor: selected ? `${SEGMENT_COLORS[seg]}1a` : 'background.paper',
                userSelect: 'none',
                transition: 'background-color 120ms, border-color 120ms',
                '&:hover': { bgcolor: `${SEGMENT_COLORS[seg]}10` },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Box
                  sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: SEGMENT_COLORS[seg] }}
                />
                <Typography variant="subtitle2">{seg}</Typography>
              </Box>
              <Typography variant="h5">{b.coaches}</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {toPercent(pct)} · {b.sessions.toLocaleString()} sessions
              </Typography>
            </Paper>
          );
        })}
      </Box>

      {/* Treemap — coaches sized by sessions, colored by transcription rate */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, flexWrap: 'wrap', mb: 1 }}>
          <Typography variant="subtitle1">Coach session distribution</Typography>
          <Typography variant="caption" color="text.secondary">
            Rectangle size = total sessions · color = transcription rate (light grey 0% → green
            100%)
            {selectedSegment ? ` · showing only "${selectedSegment}"` : ''}
          </Typography>
        </Box>
        {/* Sort controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.25 }}>
            Sort:
          </Typography>
          {[
            { key: 'sessions' as const, label: 'Sessions' },
            { key: 'rate-desc' as const, label: 'Rate (high → low)' },
            { key: 'rate-asc' as const, label: 'Rate (low → high)' },
          ].map((opt) => (
            <Chip
              key={opt.key}
              size="small"
              label={opt.label}
              variant={treemapSort === opt.key ? 'filled' : 'outlined'}
              color={treemapSort === opt.key ? 'primary' : 'default'}
              onClick={() => setTreemapSort(opt.key)}
              sx={{ height: 22, fontSize: 11 }}
            />
          ))}
        </Box>
        {treemapData.children.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            No coaches
          </Typography>
        ) : (
          <Box sx={{ height: 420 }}>
            <ResponsiveTreeMap
              data={treemapData}
              // identity must be unique per node; coach display names
              // aren't guaranteed unique. `id` is the coach's
              // person_id (or email fallback) — unique by construction.
              identity="id"
              value="value"
              valueFormat=".0f"
              // squarify is the canonical treemap algorithm — sizes are
              // visibly proportional to value. Earlier columnar layouts
              // came from unsorted input; treemapData is now sorted
              // descending in the useMemo above.
              tile="squarify"
              leavesOnly
              innerPadding={2}
              outerPadding={0}
              borderWidth={0}
              // nivo defaults nodeOpacity to 0.33 — that washes out our
              // gradient. Render cells at full intensity so the colors
              // match exactly what `rateColor` returns.
              nodeOpacity={1}
              // Hide labels on smaller cells (cells whose narrower
              // dimension is < this many px get no label).
              labelSkipSize={40}
              label={(node) => node.data.name as string}
              labelTextColor={(node) => {
                const datum = node.data as { rate?: number };
                return readableTextColor(rateColor(datum.rate ?? 0));
              }}
              parentLabelTextColor="#1f2937"
              colors={(node) => {
                const datum = node.data as { rate?: number };
                return rateColor(datum.rate ?? 0);
              }}
              animate={false}
              tooltip={({ node }) => {
                const d = node.data as {
                  name: string;
                  value: number;
                  rate: number;
                  transcribed: number;
                  segment: CoachSegment;
                };
                return (
                  <Box
                    sx={{
                      p: 1,
                      bgcolor: 'background.paper',
                      border: 1,
                      borderColor: 'divider',
                      borderRadius: 1,
                      boxShadow: 2,
                      minWidth: 200,
                    }}
                  >
                    <Typography variant="body2" fontWeight={600}>
                      {d.name}
                    </Typography>
                    <Typography variant="caption" sx={{ display: 'block' }}>
                      {d.transcribed} / {node.value} sessions ({toPercent(d.rate)})
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '2px',
                          bgcolor: SEGMENT_COLORS[d.segment],
                          flexShrink: 0,
                        }}
                      />
                      <Typography variant="caption">{d.segment}</Typography>
                    </Box>
                  </Box>
                );
              }}
            />
          </Box>
        )}
      </Paper>

      {/* Coaches table (filtered by selected segment) */}
      <Paper variant="outlined" sx={{ mb: 3 }}>
        <Box
          sx={{
            p: 2,
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          <Typography variant="subtitle1">
            Coaches ({visibleCoaches.length.toLocaleString()})
          </Typography>
          {selectedSegment && (
            <Chip
              size="small"
              label={`Segment: ${selectedSegment}`}
              onDelete={() => setSelectedSegment(null)}
              sx={{ height: 22, fontSize: 11 }}
              variant="outlined"
            />
          )}
        </Box>
        <TableContainer sx={{ maxHeight: 480 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={coachSortField === 'coachName'}
                    direction={coachSortField === 'coachName' ? coachSortDir : 'asc'}
                    onClick={() => handleCoachSort('coachName')}
                  >
                    Coach
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={coachSortField === 'segment'}
                    direction={coachSortField === 'segment' ? coachSortDir : 'asc'}
                    onClick={() => handleCoachSort('segment')}
                  >
                    Segment
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={coachSortField === 'memberCount'}
                    direction={coachSortField === 'memberCount' ? coachSortDir : 'desc'}
                    onClick={() => handleCoachSort('memberCount')}
                  >
                    Members
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={coachSortField === 'totalSessions'}
                    direction={coachSortField === 'totalSessions' ? coachSortDir : 'desc'}
                    onClick={() => handleCoachSort('totalSessions')}
                  >
                    Sessions
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={coachSortField === 'transcribedSessions'}
                    direction={coachSortField === 'transcribedSessions' ? coachSortDir : 'desc'}
                    onClick={() => handleCoachSort('transcribedSessions')}
                  >
                    Transcribed
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={coachSortField === 'rate'}
                    direction={coachSortField === 'rate' ? coachSortDir : 'desc'}
                    onClick={() => handleCoachSort('rate')}
                  >
                    Rate
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleCoaches.map((c) => (
                <TableRow key={c.coachKey} hover>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">{c.coachName}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {c.coachEmail}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={c.segment}
                      size="small"
                      sx={{
                        bgcolor: `${SEGMENT_COLORS[c.segment]}22`,
                        borderColor: SEGMENT_COLORS[c.segment],
                        color: 'text.primary',
                        height: 20,
                        fontSize: 11,
                      }}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell align="right">{c.memberCount}</TableCell>
                  <TableCell align="right">{c.totalSessions.toLocaleString()}</TableCell>
                  <TableCell align="right">{c.transcribedSessions.toLocaleString()}</TableCell>
                  <TableCell align="right">{toPercent(c.rate)}</TableCell>
                </TableRow>
              ))}
              {visibleCoaches.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} sx={{ textAlign: 'center', color: 'text.secondary' }}>
                    No coaches match
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Member-level KPIs */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <KpiCard
          label="Members ever transcribed"
          value={toPercent(memberKpis.everRate)}
          sub={`${memberKpis.everCount} of ${memberKpis.total} with ≥1 transcript`}
        />
        <KpiCard
          label="Members fully transcribed"
          value={toPercent(memberKpis.fullRate)}
          sub={`${memberKpis.fullCount} of ${memberKpis.total} (every session)`}
        />
        <KpiCard
          label="Avg per-member coverage"
          value={toPercent(memberKpis.avgRate)}
          sub="Mean of per-member rates"
        />
        <KpiCard
          label="Last session transcribed"
          value={toPercent(memberKpis.lastRate)}
          sub={`${memberKpis.lastCount} of ${memberKpis.total} members`}
        />
      </Box>

      {/* Session-level context KPIs */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        <KpiCard label="Total sessions" value={totalSessions.toLocaleString()} />
        <KpiCard
          label="Session-level transcription rate"
          value={toPercent(rate)}
          sub={`${transcribed} of ${totalSessions} sessions`}
        />
      </Box>

      {/* Member coverage distribution histogram */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1">Member coverage distribution</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Members grouped by their per-member transcription rate.{' '}
          {memberStats.length.toLocaleString()} member{memberStats.length !== 1 ? 's' : ''} total.
        </Typography>
        {memberStats.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            No members
          </Typography>
        ) : (
          <ResponsiveContainer width="100%" height={240}>
            <BarChart
              data={coverageDistribution}
              margin={{ left: 8, right: 16, top: 4, bottom: 24 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="bucket"
                tick={{ fontSize: 11 }}
                interval={0}
                angle={-30}
                textAnchor="end"
                height={48}
              />
              <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
              <Tooltip
                formatter={(value: ValueType | undefined, _name, props) => [
                  `${value} member${Number(value) === 1 ? '' : 's'} (${toPercent(props.payload.pct)})`,
                  'Members',
                ]}
              />
              <Bar dataKey="count" fill={NEUTRAL_BAR_COLOR} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Paper>

      {/* Weekly trend (aggregate view across the filtered window) */}
      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle1">Weekly transcription rate</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Session-level transcription rate per ISO week (Monday-anchored) within the filtered
          window. Hover for the sample size in each week.
        </Typography>
        {weeklyTrend.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            No sessions
          </Typography>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={weeklyTrend} margin={{ left: 8, right: 16, top: 4, bottom: 24 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="weekStart"
                tick={{ fontSize: 11 }}
                interval="preserveStartEnd"
                angle={-30}
                textAnchor="end"
                height={48}
              />
              <YAxis
                domain={[0, 1]}
                tickFormatter={(v) => `${Math.round(Number(v) * 100)}%`}
                tick={{ fontSize: 12 }}
              />
              <Tooltip
                formatter={(value: ValueType | undefined, _name, props) => [
                  value != null
                    ? `${toPercent(Number(value))} (${props.payload.transcribed}/${props.payload.total})`
                    : '—',
                  'Rate',
                ]}
                labelFormatter={(label) => `Week of ${label}`}
              />
              <Line
                type="monotone"
                dataKey="rate"
                stroke={NEUTRAL_BAR_COLOR}
                strokeWidth={2}
                dot={{ r: 3, fill: NEUTRAL_BAR_COLOR }}
                activeDot={{ r: 5 }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </Paper>

      {/* Dimension picker + session-rate breakdown */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, ml: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            Slice by:
          </Typography>
          <Box
            sx={{
              display: 'flex',
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
              overflow: 'hidden',
            }}
          >
            {(['coach', 'domain', 'partner'] as Dimension[]).map((d) => (
              <Box
                key={d}
                onClick={() => setDimension(d)}
                sx={{
                  px: 1.5,
                  py: 0.5,
                  fontSize: 12,
                  cursor: 'pointer',
                  userSelect: 'none',
                  bgcolor: dimension === d ? 'action.selected' : 'transparent',
                  fontWeight: dimension === d ? 700 : 400,
                  '&:hover': { bgcolor: 'action.hover' },
                }}
              >
                {d === 'coach' ? 'Coach' : d === 'domain' ? 'Member domain' : 'Partner'}
              </Box>
            ))}
          </Box>
        </Box>
        <BreakdownChart key={dimension} title={dimensionTitle} data={dimensionData} />
      </Box>

      {/* Members table */}
      <Paper variant="outlined" sx={{ mb: 3 }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle1">
            Members ({sortedMembers.length.toLocaleString()})
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Streak = consecutive transcribed sessions counting backward from the latest.
          </Typography>
        </Box>
        <TableContainer sx={{ maxHeight: 480 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={memberSortField === 'memberName'}
                    direction={memberSortField === 'memberName' ? memberSortDir : 'asc'}
                    onClick={() => handleMemberSort('memberName')}
                  >
                    Member
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={memberSortField === 'coachName'}
                    direction={memberSortField === 'coachName' ? memberSortDir : 'asc'}
                    onClick={() => handleMemberSort('coachName')}
                  >
                    Coach
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={memberSortField === 'partnerName'}
                    direction={memberSortField === 'partnerName' ? memberSortDir : 'asc'}
                    onClick={() => handleMemberSort('partnerName')}
                  >
                    Partner
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={memberSortField === 'totalSessions'}
                    direction={memberSortField === 'totalSessions' ? memberSortDir : 'desc'}
                    onClick={() => handleMemberSort('totalSessions')}
                  >
                    Sessions
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={memberSortField === 'transcribedSessions'}
                    direction={memberSortField === 'transcribedSessions' ? memberSortDir : 'desc'}
                    onClick={() => handleMemberSort('transcribedSessions')}
                  >
                    Transcribed
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={memberSortField === 'coverageRate'}
                    direction={memberSortField === 'coverageRate' ? memberSortDir : 'asc'}
                    onClick={() => handleMemberSort('coverageRate')}
                  >
                    Coverage
                  </TableSortLabel>
                </TableCell>
                <TableCell align="right">
                  <TableSortLabel
                    active={memberSortField === 'currentTranscribedStreak'}
                    direction={
                      memberSortField === 'currentTranscribedStreak' ? memberSortDir : 'desc'
                    }
                    onClick={() => handleMemberSort('currentTranscribedStreak')}
                  >
                    Streak
                  </TableSortLabel>
                </TableCell>
                <TableCell>Pattern</TableCell>
                <TableCell>
                  <TableSortLabel
                    active={memberSortField === 'lastSessionAt'}
                    direction={memberSortField === 'lastSessionAt' ? memberSortDir : 'desc'}
                    onClick={() => handleMemberSort('lastSessionAt')}
                  >
                    Last session
                  </TableSortLabel>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {memberPageRows.map((m) => (
                <TableRow key={m.memberKey} hover>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">
                        {m.memberName || m.memberEmail || '—'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {m.memberEmail}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{m.coachName || '—'}</TableCell>
                  <TableCell>{m.partnerName || '—'}</TableCell>
                  <TableCell align="right">{m.totalSessions}</TableCell>
                  <TableCell align="right">{m.transcribedSessions}</TableCell>
                  <TableCell align="right">
                    <Chip
                      label={toPercent(m.coverageRate)}
                      size="small"
                      color={
                        m.coverageRate === 0
                          ? 'error'
                          : m.coverageRate < 0.5
                            ? 'warning'
                            : m.coverageRate < 1
                              ? 'default'
                              : 'success'
                      }
                      variant={m.coverageRate === 1 ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    {m.currentTranscribedStreak > 0 ? m.currentTranscribedStreak : '—'}
                  </TableCell>
                  <TableCell>
                    <MemberSparkline sessions={m.sessionsAsc} />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: m.lastSessionTranscribed ? 'success.main' : 'grey.400',
                        }}
                      />
                      {m.lastSessionAt ? m.lastSessionAt.slice(0, 10) : '—'}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {memberTotalPages > 1 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5 }}>
            <Button
              size="small"
              disabled={memberPage === 0}
              onClick={() => setMemberPage((p) => p - 1)}
            >
              Prev
            </Button>
            <Typography variant="body2">
              Page {memberPage + 1} of {memberTotalPages}
            </Typography>
            <Button
              size="small"
              disabled={memberPage >= memberTotalPages - 1}
              onClick={() => setMemberPage((p) => p + 1)}
            >
              Next
            </Button>
          </Box>
        )}
      </Paper>

      {/* Sessions table */}
      <Paper variant="outlined">
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle1">Sessions ({filtered.length.toLocaleString()})</Typography>
        </Box>
        <TableContainer sx={{ maxHeight: 480 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'session_started_at'}
                    direction={sortField === 'session_started_at' ? sortDir : 'desc'}
                    onClick={() => handleSort('session_started_at')}
                  >
                    Date
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'coach_name'}
                    direction={sortField === 'coach_name' ? sortDir : 'desc'}
                    onClick={() => handleSort('coach_name')}
                  >
                    Coach
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'member_name'}
                    direction={sortField === 'member_name' ? sortDir : 'desc'}
                    onClick={() => handleSort('member_name')}
                  >
                    Member
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'partner_name'}
                    direction={sortField === 'partner_name' ? sortDir : 'desc'}
                    onClick={() => handleSort('partner_name')}
                  >
                    Partner
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={sortField === 'session_status'}
                    direction={sortField === 'session_status' ? sortDir : 'desc'}
                    onClick={() => handleSort('session_status')}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell>Transcript</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pageRows.map((r, i) => (
                <TableRow key={i} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    {r.session_started_at ? r.session_started_at.slice(0, 10) : '—'}
                  </TableCell>
                  <TableCell>{r.coach_name || r.coach_email}</TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">{r.member_name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {r.member_email}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{r.partner_name || '—'}</TableCell>
                  <TableCell>{r.session_status}</TableCell>
                  <TableCell>
                    <Chip
                      label={r.has_transcript_likely ? 'Yes' : 'No'}
                      size="small"
                      color={r.has_transcript_likely ? 'success' : 'default'}
                      variant={r.has_transcript_likely ? 'filled' : 'outlined'}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5 }}>
            <Button size="small" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
              Prev
            </Button>
            <Typography variant="body2">
              Page {page + 1} of {totalPages}
            </Typography>
            <Button
              size="small"
              disabled={page >= totalPages - 1}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </Box>
        )}
      </Paper>
    </Box>
  );
}

// ─── MultiSelect helper ───────────────────────────────────────────────────────

function MultiSelect({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: string[];
  value: string[];
  onChange: (v: string[]) => void;
}) {
  return (
    <FormControl size="small" sx={{ minWidth: 180 }}>
      <InputLabel>{label}</InputLabel>
      <Select
        multiple
        value={value}
        onChange={(e) => onChange(e.target.value as string[])}
        input={<OutlinedInput label={label} />}
        renderValue={(selected) =>
          selected.length === 1 ? selected[0] : `${selected.length} selected`
        }
      >
        {options.map((opt) => (
          <MenuItem key={opt} value={opt} dense>
            {opt}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
